import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Chip,
    Stack,
    useTheme,
    Divider,
    IconButton,
    Tooltip,
    Grid,
    CircularProgress,
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Stop as StopIcon,
    ChevronRight as DetailsIcon,
    Memory as MemoryIcon,
    Computer as ComputerIcon,
    CheckCircleOutline as ApproveIcon,
    HourglassEmpty as PendingIcon,
    ErrorOutline as ErrorIcon,
    LinkOff as DisconnectedIcon,
    Tune as ModeIcon,
} from '@mui/icons-material';

import { Device } from '../types/Device';
import { useDevices } from '../contexts/DevicesContext';
import { useDeviceRealtime } from '../hooks/useDeviceRealtime';
import { useReferenceBadge } from '../hooks/useReferenceBadges';
import StatusIndicator from './common/StatusIndicator';
import SparklineChart from './common/SparklineChart';

interface DeviceCardProps {
    device: Device;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { approveDevice, startSteering, stopSteering } = useDevices();
    const { realtimeData } = useDeviceRealtime(device.id);

    // RF interference status from realtime telemetry
    const rfStatus = realtimeData.telemetry?.interference_status ?? null;
    // red | orange when one of the device's references is not usable
    const referenceBadge = useReferenceBadge(device.id);

    // Loading states
    const [isApproving, setIsApproving] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [isStopping, setIsStopping] = useState(false);

    const formatSteeringMode = (mode: string | null | undefined): string => {
        const m = (mode || '').trim();
        if (!m) return 'N/A';
        switch (m) {
            case 'external_reference':
                return 'Ext Ref';
            case 'single_channel_gnss':
                return 'Single GNSS';
            case 'multi_channel_gnss':
                return 'Multi GNSS';
            case 'adsb':
                return 'ADSB';
            case 'gnss_adsb':
                return 'GNSS + ADSB';
            case 'open_loop':
                return 'Open Loop';
            default:
                return m;
        }
    };

    const formatController = (controllerType: string | null | undefined): string => {
        const c = (controllerType || '').trim();
        if (!c) return 'N/A';
        const lower = c.toLowerCase();
        if (lower === 'pi') return 'PI';
        if (lower === 'kalman') return 'Kalman';
        // Also support legacy uppercase values that might come from latest_control
        if (c === 'PI') return 'PI';
        if (c === 'KALMAN') return 'Kalman';
        return c;
    };

    // Get last values for sparkline
    const ocxoSamples = device.latest_status?.ocxo_control?.slice(-20) || [];
    const steeringMode = formatSteeringMode(device.steering_mode ?? null);
    const level = (device.level !== null && device.level !== undefined) ? device.level : null;
    const controller = formatController(device.latest_control?.controller_type || device.controller_type || null);

    // Handle navigation to device details
    const handleCardClick = () => {
        navigate(`/device/${device.id}`);
    };

    // Handle approve
    const handleApprove = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsApproving(true);
        try {
            await approveDevice(device.id);
        } catch (err) {
            console.error('Approve error:', err);
        } finally {
            setIsApproving(false);
        }
    };

    // Handle start
    const handleStart = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsStarting(true);
        try {
            await startSteering(device.id);
        } catch (err) {
            console.error('Start error:', err);
        } finally {
            setIsStarting(false);
        }
    };

    // Handle stop
    const handleStop = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsStopping(true);
        try {
            await stopSteering(device.id);
        } catch (err) {
            console.error('Stop error:', err);
        } finally {
            setIsStopping(false);
        }
    };

    // Determine status colors and indicators
    const getConnectionStatus = () => {
        if (device.status === 'pending_approval') return 'warning';
        if (device.status === 'idle_connected') return 'info';
        if (device.status === 'starting') return 'info';
        if (device.status === 'running') return 'success';
        if (device.status === 'stopping') return 'warning';
        if (device.status === 'error') return 'error';
        if (device.status === 'offline' || device.status === 'booting') return 'error';
        return 'neutral';
    };

    // Get device status text
    const getStatusText = () => {
        if (device.status === 'pending_approval') return 'Pending Approval';
        if (device.status === 'idle_connected') return 'Idle';
        if (device.status === 'starting') return 'Starting';
        if (device.status === 'running') return 'Running';
        if (device.status === 'stopping') return 'Stopping';
        if (device.status === 'error') return 'Error';
        if (device.status === 'offline' || device.status === 'booting') return 'Offline';
        return 'Unknown';
    };

    const getStatusChip = () => {
        let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
        let label = getStatusText();
        let icon: React.ReactElement | null = null;

        switch (device.status) {
            case 'pending_approval':
                color = 'warning';
                icon = <PendingIcon fontSize="small" sx={{ mr: 0.5 }} />;
                break;
            case 'idle_connected':
                color = 'info';
                break;
            case 'starting':
                color = 'info';
                break;
            case 'running':
                color = 'success';
                icon = <PlayIcon fontSize="small" sx={{ mr: 0.5 }}/>;
                break;
            case 'stopping':
                color = 'warning';
                break;
            case 'error':
                color = 'error';
                icon = <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />;
                break;
            case 'offline':
            case 'booting': // Treat booting as offline for card display simplicity
            default:
                color = 'error';
                label = "Offline";
                icon = <DisconnectedIcon fontSize="small" sx={{ mr: 0.5 }} />;
                break;
        }
        return <Chip icon={icon ? icon : undefined} label={label} color={color} size="small" sx={{ textTransform: 'capitalize'}} />;
    };

    return (
        <Card
            sx={{
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'visible',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: getConnectionStatus() === 'success' 
                            ? theme.palette.success.main 
                        : getConnectionStatus() === 'warning' 
                            ? theme.palette.warning.main
                            : getConnectionStatus() === 'error' 
                                ? theme.palette.error.main
                                : 'transparent',
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                },
            }}
        >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <ComputerIcon color="primary" />
                        </Box>
                        <Box>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                <StatusIndicator 
                                    status={getConnectionStatus()} 
                                    size={8} 
                                    tooltip={getStatusText()}
                                /> {device.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {level !== null ? `Level ${level}` : 'Level N/A'}
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Tooltip title="View Details">
                        <IconButton 
                            size="small" 
                            color="primary"
                            onClick={handleCardClick}
                            sx={{ 
                                bgcolor: 'rgba(58, 134, 255, 0.08)', 
                                '&:hover': { bgcolor: 'rgba(58, 134, 255, 0.15)' } 
                            }}
                        >
                            <DetailsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Metrics */}
                {device.status !== 'offline' && device.status !== 'booting' && device.status !== 'pending_approval' && (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {/* Steering Mode */}
                            <Grid item xs={6}>
                                <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <ModeIcon fontSize="small" sx={{ color: theme.palette.primary.main, mr: 0.5 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Steering Mode
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {steeringMode}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Controller Type */}
                            <Grid item xs={6}>
                                <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <MemoryIcon fontSize="small" sx={{ color: theme.palette.info.main, mr: 0.5 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Controller
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {controller}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Sparkline */}
                        {ocxoSamples.length > 0 && (
                            <Box sx={{ height: 50, mb: 2 }}>
                                <SparklineChart 
                                    data={ocxoSamples}
                                    color={getConnectionStatus() === 'success' ? theme.palette.success.main : theme.palette.primary.main}
                                />
                            </Box>
                        )}
                    </>
                )}

                <Divider sx={{ my: 1.5, mt: 'auto' }} />

                {/* Status and Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Status Indicators */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusChip()}
                            {rfStatus && (
                                <Chip
                                    label={rfStatus === 'ok' ? 'RF: OK' : `RF: ${(realtimeData.telemetry?.interference_type || 'Alert').toUpperCase()}`}
                                    size="small"
                                    color={rfStatus === 'ok' ? 'success' : 'error'}
                                    variant="outlined"
                                />
                            )}
                            {referenceBadge && (
                                <Chip
                                    label="References"
                                    size="small"
                                    color={referenceBadge === 'red' ? 'error' : 'warning'}
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {device.status === 'pending_approval' && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={isApproving ? <CircularProgress size={16} color="inherit" /> : <ApproveIcon />}
                                onClick={handleApprove}
                                disabled={isApproving}
                                fullWidth
                            >
                                {isApproving ? 'Approving...' : 'Approve Connection'}
                            </Button>
                        )}
                        {(device.status === 'idle_connected' || device.status === 'stopping') && (
                        <Button
                                variant="contained"
                                color="success"
                                startIcon={isStarting ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
                                onClick={handleStart}
                                disabled={isStarting || isApproving || isStopping}
                            fullWidth
                        >
                                {isStarting ? 'Starting...' : 'Start MODO'}
                        </Button>
                        )}
                        {(device.status === 'running' || device.status === 'starting') && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={isStopping ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
                                onClick={handleStop}
                                disabled={isStopping || isApproving || isStarting}
                                fullWidth
                            >
                                {isStopping ? 'Stopping...' : 'Stop MODO'}
                            </Button>
                        )}
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );
};

export default DeviceCard; 